import PropTypes from 'prop-types'
import { createContext, useEffect, useReducer, useCallback, useMemo } from 'react'
import axios from '../utils/axios'
import localStorageAvailable from '../utils/localStorageAvailable'

import {
  isValidToken,
  setPartner,
  setPartnerEventz,
  setSession,
  setSessionEventz,
  setTenantId,
} from './utils'
import axiosEventz from '../utils/axiosEventz'
import { NotificationService } from '../utils/notification'

export const checkIsOperator = (user) => {
  if (!user) return false
  if (user.isOperator) return true
  const role = user.roles?.[0] || user.role
  const operatorRoles = ['EVENT_USER_TICKET', 'EVENT_USER_PRODUCT', 'EVENT_USER', 'operator']
  return operatorRoles.includes(role)
}

const initialState = {
  isInitialized: false,
  isAuthenticated: false,
  user: null,
  partner: null,
  notifications: {
    isSetup: false,
    error: null,
  },
}

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      isInitialized: true,
      isAuthenticated: action.payload.isAuthenticated,
      user: action.payload.user,
      partner: action.payload.partner,
    }
  }
  if (action.type === 'LOGIN') {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
      partner: action.payload.partner,
    }
  }
  if (action.type === 'REGISTER') {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
      partner: action.payload.partner,
    }
  }
  if (action.type === 'LOGOUT') {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
    }
  }
  if (action.type === 'NOTIFICATIONS_SETUP_SUCCESS') {
    return {
      ...state,
      notifications: {
        isSetup: true,
        error: null,
      },
    }
  }
  if (action.type === 'NOTIFICATIONS_SETUP_FAILURE') {
    return {
      ...state,
      notifications: {
        isSetup: false,
        error: action.payload,
      },
    }
  }

  return state
}

export const AuthContext = createContext(null)

AuthProvider.propTypes = {
  children: PropTypes.node,
}

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState)

  const storageAvailable = localStorageAvailable()

  const initialize = useCallback(async () => {
    try {
      const accessToken = storageAvailable ? localStorage.getItem('accessToken') : ''
      const selectedPartnerId = localStorage.getItem('partner')

      // Restore Eventz tokens
      const accessTokenEventz = storageAvailable ? localStorage.getItem('accessTokenEventz') : ''
      const tenantId = storageAvailable ? localStorage.getItem('tenantId') : ''
      if (accessTokenEventz) setSessionEventz(accessTokenEventz)
      if (tenantId) setTenantId(tenantId)

      // Operator restore logic:
      const localUserString = storageAvailable ? localStorage.getItem('user') : ''
      const localUser = localUserString ? JSON.parse(localUserString) : null
      const isOperator = checkIsOperator(localUser)

      if (isOperator && accessTokenEventz) {
        setSessionEventz(accessTokenEventz)
        const response = await axiosEventz.get('/oauth/operator/me')
        const user = response.data

        dispatch({
          type: 'INITIAL',
          payload: {
            isAuthenticated: true,
            user: {
              ...user,
              isOperator: true,
            },
            partner: null,
          },
        })
      } else if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken)
        setPartner(selectedPartnerId)

        const response = await axios.get('/oauth/me')
        const user = response.data

        let partner =
          user?.partners?.find((p) => p.id === selectedPartnerId) ||
          user?.partners?.find((p) => p.id === user?.partner_id_eventz) ||
          user?.partners?.find(Boolean)

        if (!partner?.id && user?.roles.includes('WHITELABEL')) {
          partner = { id: selectedPartnerId }
        }

        setPartner(partner?.id)

        dispatch({
          type: 'INITIAL',
          payload: {
            isAuthenticated: true,
            user,
            partner,
          },
        })
      } else {
        dispatch({
          type: 'INITIAL',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        })
      }
    } catch (error) {
      console.error(error)
      dispatch({
        type: 'INITIAL',
        payload: {
          isAuthenticated: false,
          user: null,
        },
      })
    }
  }, [storageAvailable])

  useEffect(() => {
    initialize()
  }, [initialize])

  // LOGIN
  const login = useCallback(async (data) => {
    const response = await axios.post('/oauth/token', data)
    const { access_token, user } = response.data

    const selectedPartnerId = localStorage.getItem('partner')
    const partner =
      user?.partners?.find((p) => p.id === selectedPartnerId) || user?.partners?.find(Boolean)

    setPartner(partner?.id)
    setSession(access_token)
    localStorage.setItem('user', JSON.stringify(user))

    // Set Eventz tokens
    if (user.access_token_eventz) setSessionEventz(user.access_token_eventz)
    if (user.tenant_id_eventz) setTenantId(user.tenant_id_eventz)
    if (user.partner_id_eventz) setPartnerEventz(user.partner_id_eventz)

    dispatch({
      type: 'LOGIN',
      payload: {
        user: {
          ...user,
          partner,
        },
        partner,
      },
    })

    if (process.env.NODE_ENV === 'production') {
      try {
        await NotificationService.setupNotifications()
        dispatch({ type: 'NOTIFICATIONS_SETUP_SUCCESS' })
      } catch (error) {
        console.error('Erro ao configurar notificações', error)
        dispatch({
          type: 'NOTIFICATIONS_SETUP_FAILURE',
          payload: error,
        })
      }
    }
  }, [])

  const operatorLogin = useCallback(async (data) => {
    const response = await axiosEventz.post('/oauth/operator/token', data)
    const { access_token, user } = response.data

    setSessionEventz(access_token)
    const operatorUser = {
      ...user,
      isOperator: true,
    }
    localStorage.setItem('user', JSON.stringify(operatorUser))

    const selectedPartnerId = localStorage.getItem('partnerEventz')
    const partner =
      user?.partners.find((p) => p.id === selectedPartnerId) || user?.partners.find(Boolean)

    const eventCode = data.event_code
    localStorage.setItem('event-code', eventCode)
    setPartnerEventz(partner?.id)

    dispatch({
      type: 'LOGIN',
      payload: {
        user: operatorUser,
        partner: null,
      },
    })

    return operatorUser
  }, [])

  const impersonate = useCallback(
    async (partnerID) => {
      setPartner(partnerID)
      await initialize()
    },
    [initialize]
  )

  const register = useCallback(async (email, password, firstName, lastName) => {
    const response = await axios.post('/api/account/register', {
      email,
      password,
      firstName,
      lastName,
    })
    const { accessToken, user } = response.data

    const selectedPartnerId = localStorage.getItem('partner')

    const partner =
      user?.partners?.find((p) => p.id === selectedPartnerId) ||
      user?.partners?.find((p) => p.id === user?.partner_id_eventz) ||
      user?.partners?.find(Boolean)

    setSession(accessToken)
    setPartner(partner?.id)
    localStorage.setItem('user', JSON.stringify(user))

    dispatch({
      type: 'REGISTER',
      payload: {
        user,
        partner,
      },
    })
  }, [])

  // LOGOUT
  const logout = useCallback(() => {
    setSession(null)
    setPartner(null)
    setPartnerEventz(null)
    setSessionEventz(null)
    setTenantId(null)
    localStorage.removeItem('event-code')
    localStorage.removeItem('user')
    localStorage.removeItem('operator-selected-session')
    localStorage.removeItem('operator-session-id')
    localStorage.removeItem('operator-selected-sector')
    localStorage.removeItem('operator-sector-id')
    sessionStorage.removeItem('whiteLabelSettings')
    dispatch({
      type: 'LOGOUT',
    })
  }, [])

  const memoizedValue = useMemo(
    () => ({
      isInitialized: state.isInitialized,
      isAuthenticated: state.isAuthenticated,
      user: state.user,
      isOperator: checkIsOperator(state.user),
      partner: state.partner,
      method: 'jwt',
      login,
      operatorLogin,
      register,
      logout,
      impersonate,
    }),
    [
      state.isAuthenticated,
      state.isInitialized,
      state.user,
      state.partner,
      login,
      operatorLogin,
      logout,
      register,
      impersonate,
    ]
  )

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>
}
